export interface Integration {
  id: string;
  company_id: string;
  name: string;
  type: IntegrationGroupType;
  group: string;
  options_json: string | null;
  is_active: boolean;
}

export type PaymentIntegrationGroupType =
  | 'mollie'
  | 'pay'
  | 'nationale-bioscoopbon'
  | 'all-in'
  | 'giftcard'
  | 'gift-card'
  | 'cash'
  | 'etender'
  | 'webflow'
  | 'sepa-direct-debit';

export type SiteSyncIntegrationGroupType = 'webflow' | 'tms-schedule-feed';
export type ReportingIntegrationGroupType = 'maccsbox' | 'metabase';
export type EmailIntegrationGroupType = 'mailgun';
export type AccountingIntegrationGroupType = 'exact-online';
export type IntegrationGroupType =
  | 'etender'
  | PaymentIntegrationGroupType
  | SiteSyncIntegrationGroupType
  | ReportingIntegrationGroupType
  | EmailIntegrationGroupType
  | AccountingIntegrationGroupType;

export const IntegrationGroups = {
  Payments: 'payments',
  RestaurantReservation: 'restaurant-reservation',
  SiteSync: 'site-sync',
  Reporting: 'reporting',
  Email: 'email',
  Accounting: 'accounting',
} as const;
export type IntegrationGroup = (typeof IntegrationGroups)[keyof typeof IntegrationGroups];

export interface WebflowIntegrationOptions {
  version: 'v1';
  site_id: string;
  token: string;
  max_items_per_collection?: number | null;
  // Public domain of the customer's site, without scheme or trailing slash. The backend composes
  // movie and newsletter page URLs as {site_domain}/{slug}.
  site_domain?: string | null;
  // Set once when the Webflow site is wired up, never rendered by the wizard. Listed so the option
  // bag stays an honest mirror of the backend model.
  movies_collection_page_slug?: string | null;
  publish_retry_delay_seconds?: number | null;
  // Resized copies of the photos editors upload to the site's CMS (PAUL-3754).
  photo_mirror?: WebflowPhotoMirrorOptions | null;
  // Signing keys of the photo mirror's webhooks, by webhook id. Vault-held like `token` and merged into
  // the GET response; echo them back on save, never render them.
  webhook_secrets?: Record<string, string> | null;
}

export interface WebflowPhotoMirrorOptions {
  enabled: boolean;
  // Image fields left at full size, as `collection-slug.field-slug`.
  exclude_fields?: string[];
  // Written by the backend when it registers the webhooks; echo it back on save, never render it.
  webhooks?: WebflowPhotoMirrorWebhook[];
}

export interface WebflowPhotoMirrorWebhook {
  id: string;
  trigger_type: string;
  url: string;
}

export interface SepaIntegrationOptions {
  // The integration's own Pay.nl account (typically the Incasso-enabled sales location),
  // mirroring the pay integration's credential fields.
  key: string;
  at: string;
  service_id: string;
  test_mode?: boolean;
  terms_and_conditions_url: string;
  bank_statement_descriptor: string;
  mandate_grace_days?: number | null;
  description_html?: string | null;
  enabled: boolean;
}

export interface MaccsboxIntegrationOptions {
  exhibitor_id: string;
  user: string;
  password: string;
  enabled: boolean;
  daily_report_hour?: number | null;
  weekly_report_day?: number | null;
  weekly_report_hour?: number | null;
  daily_report_auto_approval: boolean;
  weekly_report_auto_approval: boolean;
}

/** The period a Metabase dashboard opens on; weeks are play weeks (Thursday-Wednesday). */
export const METABASE_DATE_PRESETS = [
  'none',
  'fixed',
  'current_day',
  'previous_day',
  'next_day',
  'current_week',
  'previous_week',
  'next_week',
  'current_month',
  'previous_month',
  'next_month',
  'current_year',
  'previous_year',
  'next_year',
] as const;
export type MetabaseDatePreset = (typeof METABASE_DATE_PRESETS)[number];

/** One dashboard of a `metabase` integration. Array order is menu order. */
export interface MetabaseDashboardOption {
  /** Generated by the FE, stable for the life of the entry: menu links and the embed route use it. */
  id: string;
  enabled: boolean;
  /** The Metabase dashboard number; required (> 0) when the entry is enabled. */
  dashboard_id: number | null;
  name_en: string;
  name_nl: string;
  /** Slug of a date-range parameter to prefill; empty = no prefill. */
  date_parameter: string;
  date_preset: MetabaseDatePreset;
  /** `yyyy-MM-dd`, only for `fixed`. */
  date_from: string | null;
  date_to: string | null;
}

/** `options_json` of a `metabase` integration (super admin only). */
export interface MetabaseIntegrationOptions {
  enabled: boolean;
  dashboards: MetabaseDashboardOption[];
}

export type ExactVatMode = 'derived' | 'explicit';

export interface ExactVatCode {
  tax_id: string;
  code: string;
}

/** `options_json` of an `exact-online` integration: the codes the journal's Exact export needs. No credentials. */
export interface ExactOnlineIntegrationOptions {
  enabled: boolean;
  vat_codes: ExactVatCode[];
  no_vat_code: string;
  debtor_relation: { code: string; name: string | null };
  vat_mode: ExactVatMode;
}

/**
 * A TMS feed key as the key endpoints return it. There is deliberately no `key` field: only a
 * SHA-256 hash is stored, so there is nothing to return and nothing to mask.
 */
export interface TmsApiKey {
  id: string;
  label: string;
  key_prefix: string;
  created_at: string;
}

/** The same key as it appears inside `options_json`. `key_hash` is round-tripped, never read. */
export interface TmsApiKeyOption extends TmsApiKey {
  key_hash: string;
}

/** Only `POST /integrations/tms/{id}/keys` ever carries the plaintext key, and only once. */
export interface TmsApiKeyCreated extends TmsApiKey {
  key: string;
}

export interface TmsScheduleFeedIntegrationOptions {
  shop_id: string;
  enabled: boolean;
  // Owned by the three key endpoints. Echo it back on save; never build it from form state.
  api_keys: TmsApiKeyOption[];
}

export interface MailgunIntegrationOptions {
  api_key: string;
  region: 'eu' | 'us';
  webhook_signing_key: string;
  marketing_api_key?: string;
  subaccount_id?: string;
}

export interface BaseIntegrationOptions {
  name: string;
  key?: string;
  at?: string;
  service_id?: string;
  test_mode?: boolean;
  clearing_key?: string;
  clearing_at?: string;
  clearing_service_id?: string;
  clearing_merchant_id?: string;
}

export interface NationaleBioscoopbonOptions {
  client_id?: string;
  client_password?: string;
  requestor?: string;
  source?: string;
  test_mode?: boolean;
  madiwodo_client_id?: string;
  madiwodo_client_password?: string;
}

export enum NationaleBioscoopbonSource {
  POS = 'POS',
  INTERNET = 'INTERNET',
}

export const NationaleBioscoopbonSourceSelectOptions = [
  { key: 'POS', value: NationaleBioscoopbonSource.POS },
  { key: 'INTERNET', value: NationaleBioscoopbonSource.INTERNET },
];
